import { computed, ref, useContext, watch, reactive } from '@nuxtjs/composition-api';
import type { Ref } from '@nuxtjs/composition-api';
import { isEqual } from 'lodash-es';
import { useCategories } from './categories';
import { Aggregation, AggregationOption } from '~/graphql-types.gen';
import { ProductsQuery } from '~/graphql/Product';
import { lastItem, toNonDuplicateArrayBy, toNonNullable } from '~/utils/collections';

export interface MappedAggregation {
  label: string;
  type: string;
  code: string;
  options: AggregationOption[] | number[];
  value: AggregationOption | AggregationOption[] | null | undefined;
}

interface FacetComposableOptions {
  exclude?: string[];
  only?: string[];
}

export function useFacets(
  aggregations: Ref<NonNullable<ProductsQuery['connection']>['aggregations']>,
  opts?: FacetComposableOptions
) {
  const { flatCategories } = useCategories();

  const exclude = opts?.exclude ?? [];

  const facets = ref([] as MappedAggregation[]);
  const categories = ref([] as MappedAggregation[]);

  // exception for facets prices
  // predefine price facets limits

  const price = reactive<{
    from: number | undefined;
    to: number | undefined;
  }>({
    from: undefined,
    to: undefined,
  });

  const { query, app } = useContext();

  const blacklisted = [''];

  watch(
    aggregations,
    (value, beforeValue) => {
      /**
       *
       * This is price lock flag to hold price aggregation when we got zero results
       *
       *
       * corner case #1: when we get a zero result that causes the filters to reset to the base state, so this triggers another fetch request that returns a collection of products with filters
       * that filters get mapped to our selections so it resets the filter an repeat this all over again causing a loop
       * so it order to mitigate that , we lock the price facet incase of the out of range result appeared
       *
       *
       */
      const priceLock =
        beforeValue?.length && !value?.length ? beforeValue?.find(item => item?.attribute_code === 'price') : undefined;
      const priceOptions = toNonNullable(
        aggregations.value?.find(option => option?.attribute_code === 'price')?.options
      );
      const lastPriceValue = toNonNullable(priceOptions[priceOptions.length - 1]?.value.split('_'));
      if (!price.to) {
        price.from = 0;
        price.to = Number(lastPriceValue[lastPriceValue.length - 1]);
      }

      facets.value = toNonNullable(priceLock ? [priceLock] : value)
        .map(aggregation => ({ ...aggregation, attribute_code: resolveFacetCode(aggregation) }))
        .filter(aggregation => {
          return (
            !exclude.includes(aggregation?.attribute_code) &&
            (opts?.only && opts?.only.length > 0 ? opts?.only.includes(aggregation?.attribute_code) : true)
          );
        })
        .map(aggregation =>
          mapAggregation(
            aggregation,
            query.value,

            /**
             * lock the price facet range between from and to values that comes from the first listing query
             * so we prevent the slider from being changing depending on the value we select that might end us up to a loop
             *
             */
            aggregation.attribute_code === 'price' && price.to
              ? {
                  price: [0, price.to],
                }
              : undefined
          )
        )
        .map(aggregation =>
          blacklisted.includes(aggregation.code) ? aggregation : mapAggregationValue(aggregation, facets.value)
        );
    },
    {
      immediate: true,
      deep: true,
    }
  );

  const productQueryFilters = computed(() => {
    return facets.value.reduce((acc, facet) => {
      const value = facet.value;
      if (!(Array.isArray(value) ? value.length : value)) {
        return acc;
      }

      // Skip including price if it already has the value of the existing min/max range.
      if (facet.code === 'price' && value?.toString() === facet.options.toString()) {
        return acc;
      }

      if (facet.code === 'price' && Array.isArray(value)) {
        acc.price = {
          from: value[0],
          to: value[1],
        };

        /**
         * if price range is already defined
         * set the value so the price facet can be locked to a certain value
         */
        if (!price.to) {
          price.from = 0;
          price.to = Number(lastItem(facet.options as number[]));
        }
        return acc;
      }

      if (Array.isArray(value) && value.length) {
        acc[facet.code] = { in: value.map(option => option.value) };

        return acc;
      }

      if (!Array.isArray(value)) {
        acc[facet.code] = { eq: value?.value };
      }

      return acc;
    }, {} as Record<string, any>);
  });

  const urlQueryParams = computed(() => {
    return facets.value.reduce((acc, facet) => {
      if (!facet.value) {
        return acc;
      }

      if (facet.code === 'price') {
        const from = (facet.value as any)[0];
        const to = (facet.value as any)[1];

        if (from !== facet.options[0] || to !== facet.options[1]) {
          acc.price_from = from;
          acc.price_to = to;
        }

        return acc;
      }

      acc[facet.code] = Array.isArray(facet.value) ? [facet.value.map(option => option.value)] : facet.value.value;

      return acc;
    }, {} as Record<string, any>);
  });

  // function removeQueryParam(){
  // }

  watch(
    facets,
    (newValue, oldFacets) => {
      let whiteListedParams: RegExp[] = [/category_parent_id/, /q/, /sort/, /utm_/, /fbclid/, /gclid/];

      // Initiating or recreating the facets
      if (!oldFacets.length) {
        whiteListedParams = [...whiteListedParams, /page/];
      }

      const currentQuery = app.router?.currentRoute.query;

      const query = { ...fromFacetToUrlParams(newValue) };

      const filteredQuery = Object.keys((currentQuery as unknown) as Record<string, any>)
        .filter(query => whiteListedParams.some(regex => regex.test(query)))
        .reduce((obj, key) => {
          return {
            ...obj,
            [key]: currentQuery && currentQuery[key],
          };
        }, {});

      app.router
        ?.replace({
          query: {
            ...filteredQuery,
            ...query,
          },
        })
        .catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // throw err;
          }
        });
    },
    {
      deep: true,
    }
  );

  // facet functions

  function removeItem({ code, value }: { code: string; value: string }) {
    if (code === 'price') {
      facets.value = facets.value.map(facet =>
        facet.code === 'price'
          ? {
              ...facet,
              value: facet?.options as any,
            }
          : facet
      );

      return;
    }

    facets.value = facets.value.map(facet =>
      facet.code === code
        ? {
            ...facet,
            value: Array.isArray(facet.value)
              ? facet.value.filter(itemValue => itemValue.value !== value)
              : value === facet.value?.value
              ? []
              : facet.value,
          }
        : facet
    );
  }

  // Get categories from first level only
  const firstLevelCategoryOptions = computed(() => {
    const categoryFacet = facets.value.find(facet => facet.code === 'category_uid');

    if (!categoryFacet) {
      return [];
    }

    return (categoryFacet.options as Array<AggregationOption>).filter((option: AggregationOption) =>
      flatCategories.value.find(category => category.uid === option.value && category.level === 1)
    );
  });

  function clearAll() {
    facets.value = facets.value.map(facet => {
      if (facet.type === 'price') {
        facet.value = (facet as any).options;
        return facet;
      }
      return { ...facet, value: [] };
    });
  }

  function setFacetValue({ code, value }: { code: string; value: AggregationOption }) {
    facets.value = facets.value.map(facet =>
      facet.code === code
        ? {
            ...facet,
            value: Array.isArray(facet.value) ? toNonDuplicateArrayBy([...facet.value, value], 'value') : [value],
          }
        : facet
    );
  }

  function getFacetValues(code: string) {
    return facets.value.filter(item => item.code === code)[0]?.options || [];
  }

  return {
    facets,
    categories,
    filters: productQueryFilters,
    urlQueryParams,
    firstLevelCategoryOptions,
    setFacetValue,
    getFacetValues,
    removeItem,
    clearAll,
  };
}

/**
 * Maps an aggregation item
 */
export function mapAggregation(
  apiItem: Aggregation,
  defaultValues: Record<string, string | (string | null)[]>,

  /**
   * defining a third optional Parameter to adjust the facet aggregation option to some specific value
   */
  defaultOptions?: Record<string, AggregationOption[] | number[]>
): MappedAggregation {
  const attributeValues = defaultValues[apiItem.attribute_code]
    ? Array.isArray(defaultValues[apiItem.attribute_code])
      ? defaultValues[apiItem.attribute_code]
      : (defaultValues[apiItem.attribute_code] as string).split(',')
    : [];
  const result: MappedAggregation = {
    // set label from map else set it to item's label
    label: apiItem.label as string,
    type: resolveFacetType(apiItem),
    code: apiItem.attribute_code,
    options: [],
    value:
      apiItem.options?.reduce<any[]>((accu, opt) => {
        return attributeValues.includes(opt?.value || '') ? [...accu, opt] : accu;
      }, []) || undefined,
  };

  const options = toNonNullable(apiItem.options);
  if (apiItem.attribute_code === 'price') {
    // if we have a price attribute
    // set the options range to be from  0 to the last boundary in the last array element
    // result.options = [0, Number(lastItem(options).value.split('_')[1])];
    result.options = defaultOptions?.price
      ? [0, Number(lastItem(defaultOptions?.price as number[]))]
      : [0, Number(lastItem(options).value.split('_')[1])];
  } else {
    result.options = options;
  }

  if (result.code === 'price') {
    result.value = [
      (Number(defaultValues.price_from) as any) || result.options[0],
      (Number(defaultValues.price_to) as any) || result.options[1],
    ];
  }

  if (result.code === 'product_brand_id') {
    result.options.sort((a: AggregationOption | number, b: AggregationOption | number) => {
      if (typeof a !== 'number' && a.label && typeof b !== 'number' && b.label) {
        return a.label.localeCompare(b.label);
      }
      return 0;
    });
  }

  return result;
}

/**
 *
 * Maps an aggregation value
 */
export function mapAggregationValue(aggregation: MappedAggregation, facets: MappedAggregation[]): MappedAggregation {
  if (aggregation.code === 'price') {
    const facetValue: AggregationOption[] | AggregationOption =
      facets.find(facet => facet.code === aggregation.code)?.value ?? [];

    return { ...aggregation, value: (facetValue as []).length ? facetValue : aggregation.value };
  }

  const facetValue: AggregationOption[] | AggregationOption =
    facets.find(facet => facet.code === aggregation.code)?.value ?? [];

  const aggregationValue =
    (aggregation.value && (Array.isArray(aggregation.value) ? aggregation.value : [aggregation.value])) ?? [];

  return {
    ...aggregation,
    value: [...(Array.isArray(facetValue) ? facetValue : [facetValue]), ...aggregationValue],
  };
}

const FACET_TYPE_MAP = {
  price: 'price',
  toggle: 'toggle',
  hidden: 'hidden',
  '*': 'select',
};

/**
 * called when mapping the facet items in mapFacet
 * handles setting the type attribute for each facet based on facet's attribute
 * @param {attribute: String, label: String, count: number, options: Array} apiItem
 */
function resolveFacetType(apiItem: Aggregation) {
  if (apiItem.attribute_code === 'price') {
    return FACET_TYPE_MAP[apiItem.attribute_code];
  }

  if (apiItem.attribute_code === 'has_deal' && apiItem.options?.find(option => option?.value === '1')) {
    return FACET_TYPE_MAP.toggle;
  }

  if (apiItem?.options?.length === 1 && ['1', '0', 'yes', 'no'].includes(apiItem?.options?.[0]?.value || '')) {
    return FACET_TYPE_MAP.hidden;
  }

  if (isEqual(apiItem?.options?.map(option => option?.value).sort(), ['0', '1'])) {
    return FACET_TYPE_MAP.toggle;
  }

  return FACET_TYPE_MAP['*'];
}

/**
 * !this one is a resolver to overcome the side effects the might happen from magento api
 * cases like when we want to parse an aggregation option that send from the api as x and wanted us to return it as y
 * example: category_uid aggregation sent to us like that , then we deal with it transferred as category_id instead and continue
 * */
function resolveFacetCode(apiItem: Aggregation) {
  const codeMapper: Record<string, string> = {
    // category_uid: 'category_id',
  };

  return codeMapper[apiItem.attribute_code] ? codeMapper[apiItem.attribute_code] : apiItem.attribute_code;
}

export const isFilterableFacet = (facet: MappedAggregation) => {
  if (facet.code === 'price') {
    const from = (facet.value as any)[0];
    const to = (facet.value as any)[1];

    if (from !== facet.options[0] || to !== facet.options[1]) {
      return true;
    }
    return false;
  }
  return facet.code !== 'price' && (facet?.value as AggregationOption[])?.length;
};

function fromFacetToUrlParams(facets: MappedAggregation[]) {
  return facets.reduce((acc, facet) => {
    if (!facet) {
      return acc;
    }

    if (facet.code === 'price') {
      const from = (facet.value as any)[0];
      const to = (facet.value as any)[1];

      if (from !== facet.options[0] || to !== facet.options[1]) {
        acc.price_from = from;
        acc.price_to = to;
      }

      return acc;
    }

    if (Array.isArray(facet.value) && facet.value.length === 0) {
      delete acc[facet.code];
      return acc;
    }

    acc[facet.code] = Array.isArray(facet.value)
      ? [facet.value.map(option => option.value)]
      : facet.value?.value
      ? facet.value.value
      : facet.value;

    return acc;
  }, {} as Record<string, any>);
}

export const updateFacetValue = (facets: MappedAggregation[]) => ({
  code,
  value,
}: {
  code: string;
  value: AggregationOption;
}): MappedAggregation[] => {
  return facets.map(facet =>
    facet.code === code
      ? {
          ...facet,
          value: Array.isArray(facet) ? toNonDuplicateArrayBy([...facet, value], 'value') : [value],
        }
      : facet
  );
};

export const removeFacetValue = (facets: MappedAggregation[]) => ({ code }: { code: string }): MappedAggregation[] => {
  return facets.map(facet =>
    facet.code === code
      ? {
          ...facet,
          value: Array.isArray(facet) ? toNonDuplicateArrayBy([...facet], '') : [],
        }
      : facet
  );
};
